import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchProducts, fetchCategories } from './api';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  IconButton, 
  useTheme,
  useMediaQuery,
  Typography,
  Chip,
  Paper,
  alpha,
  Fade,
  InputBase,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import config from './config';
import { useCart } from './CartContext';
import { isBranchOpen } from './branchUtils';

function SearchBarEnhanced() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const searchText = 'أبحث عن المنتجات و العروض...';
  const [animatedText, setAnimatedText] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // ✅ Cache products & categories to avoid re-fetching on every keystroke
  const productsCache = useRef(null);
  const categoriesCache = useRef(null);
  const offerCategoryIdsCache = useRef(null);

  const typingRef = useRef(1);
  const inputRef = useRef(null);
  const { selectedBranch } = useCart();
  const [branchStatus, setBranchStatus] = useState({ isOpen: true });

  useEffect(() => {
    if (selectedBranch) {
      setBranchStatus(isBranchOpen(selectedBranch));
    }
  }, [selectedBranch]);

  // ✅ Pre-load products when component mounts (not on every search)
  useEffect(() => {
    const preloadData = async () => {
      try {
        const [products, categories] = await Promise.all([
          fetchProducts(selectedBranch?.id),
          fetchCategories(selectedBranch?.id)
        ]);
        productsCache.current = products;
        categoriesCache.current = categories;
        offerCategoryIdsCache.current = categories
          .filter(cat => /^(العروض:|offer|deal|مجموعة:)/i.test(cat.CategoryName))
          .map(cat => cat.Id);
      } catch {
        productsCache.current = [];
        categoriesCache.current = [];
        offerCategoryIdsCache.current = [];
      }
    };
    preloadData();
  }, [selectedBranch]);

  // Typing animation
  useEffect(() => {
    let timeoutId;
    const type = () => {
      setAnimatedText(searchText.slice(0, typingRef.current));
      if (typingRef.current < searchText.length) {
        typingRef.current++;
        timeoutId = setTimeout(type, 120);
      } else {
        timeoutId = setTimeout(() => {
          typingRef.current = 1;
          type();
        }, 1200);
      }
    };
    type();
    return () => clearTimeout(timeoutId);
  }, []);

  // ✅ Robust image URL resolver
  const getProductImageUrl = (product) => {
    const rawImage =
      product.ProductImage ??
      product.ImageUrl ??
      product.Image ??
      product.productImage ??
      product.imageUrl ??
      product.image ??
      null;

    if (!rawImage) return null;

    if (typeof rawImage === 'string' && (rawImage.startsWith('{') || rawImage.startsWith('['))) {
      try {
        const parsed = JSON.parse(rawImage);
        return parsed.medium || parsed.thumbnail || parsed.small || parsed.original || parsed.large || null;
      } catch {
        const mediumMatch = rawImage.match(/"medium"\s*:\s*"(https?:\/\/[^"]+)"/i);
        if (mediumMatch?.[1]) return mediumMatch[1];
        const thumbnailMatch = rawImage.match(/"thumbnail"\s*:\s*"(https?:\/\/[^"]+)"/i);
        if (thumbnailMatch?.[1]) return thumbnailMatch[1];
        const anyUrl = rawImage.match(/https?:\/\/[^\s"'}\]]+\.(jpg|jpeg|png|gif|webp)/i);
        if (anyUrl?.[0]) return anyUrl[0];
        return null;
      }
    }

    if (typeof rawImage === 'string') {
      if (rawImage.startsWith('http://') || rawImage.startsWith('https://')) return rawImage;
      if (rawImage.startsWith('/images/') || rawImage.startsWith('/uploads/')) return `${config.SITE_BASE_URL}${rawImage}`;
      return `${config.SITE_BASE_URL}/${rawImage}`;
    }

    if (typeof rawImage === 'object' && rawImage !== null && !Array.isArray(rawImage)) {
      return rawImage.medium || rawImage.thumbnail || rawImage.small || rawImage.original || rawImage.large || null;
    }

    return null;
  };

  if (!branchStatus.isOpen) return null;

  // ✅ FIXED: Search from cache, no re-fetching
  const handleSearch = (e) => {
    const value = e.target.value;
    setQuery(value);

    if (value.trim().length > 1) {
      const products = productsCache.current || [];
      const offerIds = offerCategoryIdsCache.current || [];

      const filtered = products.filter(p =>
        !offerIds.includes(p.CategoryId) &&
        (!Array.isArray(p.SubProducts) || p.SubProducts.length === 0) &&
        p.ProductName.toLowerCase().includes(value.toLowerCase())
      );
      setResults(filtered);
    } else {
      setResults([]);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  // ✅ FIXED: Scroll to product, hide results after scroll
  const handleProductClick = (productId) => {
    // 1. Hide results immediately
    setQuery('');
    setResults([]);
    setIsFocused(false);

    // 2. Small delay to let results disappear before scrolling
    setTimeout(() => {
      const element = document.getElementById(`product-${productId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // 3. Highlight the product briefly
        element.style.transition = 'box-shadow 0.3s ease';
        element.style.boxShadow = `0 0 0 3px ${theme.palette.primary.main}`;
        setTimeout(() => {
          element.style.boxShadow = '';
        }, 2000);
      }
    }, 100);
  };

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: 900, 
      mx: 'auto', 
      mt: { xs: 2, sm: 3, md: 4 }, 
      mb: { xs: 2, sm: 3, md: 4 }, 
      px: { xs: 1.5, sm: 2, md: 3 }, 
      direction: 'rtl',
      position: 'relative'
    }}>

      {/* Search Input */}
      <Paper
        elevation={isFocused ? 6 : 2}
        sx={{
          borderRadius: { xs: 3, sm: 4 },
          transition: 'all 0.3s ease',
          border: '2px solid',
          borderColor: isFocused ? theme.palette.primary.main : 'transparent',
          overflow: 'hidden'
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          px: { xs: 1.5, sm: 2 },
          py: { xs: 0.5, sm: 1 },
          gap: 1,
          direction: 'rtl'
        }}>
          <Box sx={{
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            borderRadius: '50%',
            bgcolor: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {loading
              ? <CircularProgress size={18} sx={{ color: 'white' }} />
              : <SearchIcon sx={{ color: 'white', fontSize: { xs: 18, sm: 22 } }} />
            }
          </Box>

          <InputBase
            inputRef={inputRef}
            fullWidth
            value={query}
            onChange={handleSearch}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setTimeout(() => setIsFocused(false), 200)}
            placeholder={animatedText + (animatedText.length === searchText.length ? '' : '|')}
            inputProps={{
              style: {
                direction: 'rtl',
                textAlign: 'right',
                fontSize: isMobile ? 15 : 18,
                fontFamily: 'Cairo, sans-serif',
                color: theme.palette.text.primary
              }
            }}
            sx={{ flex: 1, py: { xs: 0.8, sm: 1 } }}
          />

          {query.length > 0 && (
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Paper>

      {/* Results Count */}
      {results.length > 0 && (
        <Fade in={results.length > 0}>
          <Box sx={{
            mt: 1.5,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1
          }}>
            <Chip
              label={`${results.length} نتيجة`}
              size="small"
              color="primary"
              sx={{
                fontFamily: 'Cairo, sans-serif',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.85rem' }
              }}
            />
          </Box>
        </Fade>
      )}

      {/* Results Grid */}
      {results.length > 0 && (
        <Fade in={results.length > 0}>
          <Grid
            container
            spacing={{ xs: 1.5, sm: 2, md: 3 }}
            sx={{ mt: 0.5, direction: 'rtl' }}
          >
            {results.map(prod => {
              const imageUrl = getProductImageUrl(prod);
              const hasError = imageErrors[prod.Id];

              return (
                <Grid item xs={6} sm={6} md={4} key={prod.Id}>
                  <Card
                    onClick={() => handleProductClick(prod.Id)}
                    sx={{
                      borderRadius: { xs: 2, sm: 3 },
                      boxShadow: 3,
                      transition: 'all 0.25s ease',
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        transform: isMobile ? 'none' : 'translateY(-6px)',
                        boxShadow: 8,
                        borderColor: theme.palette.primary.main
                      },
                      '&:active': { transform: 'scale(0.97)' }
                    }}
                  >
                    {/* Product Image */}
                    <Box sx={{
                      position: 'relative',
                      height: { xs: 120, sm: 150, md: 180 },
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      overflow: 'hidden',
                      borderRadius: '8px 8px 0 0'
                    }}>
                      {imageUrl && !hasError ? (
                        <CardMedia
                          component="img"
                          image={imageUrl}
                          alt={prod.ProductName}
                          onError={() =>
                            setImageErrors(prev => ({ ...prev, [prod.Id]: true }))
                          }
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease',
                            '&:hover': { transform: isMobile ? 'none' : 'scale(1.05)' }
                          }}
                        />
                      ) : (
                        <Box sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <ImageNotSupportedIcon sx={{
                            fontSize: { xs: 40, sm: 52 },
                            color: alpha(theme.palette.primary.main, 0.3)
                          }} />
                        </Box>
                      )}
                    </Box>

                    {/* Product Info */}
                    <CardContent sx={{
                      textAlign: 'right',
                      direction: 'rtl',
                      p: { xs: 1.2, sm: 2 },
                      '&:last-child': { pb: { xs: 1.2, sm: 2 } },
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <Box>
                        <Typography
                          fontWeight={700}
                          fontFamily="Cairo, sans-serif"
                          color="text.primary"
                          sx={{
                            fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                            mb: 0.5,
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {prod.ProductName}
                        </Typography>

                        {prod.Description && (
                          <Typography
                            color="text.secondary"
                            fontFamily="Cairo, sans-serif"
                            sx={{
                              fontSize: { xs: '0.75rem', sm: '0.85rem' },
                              mb: 0.8,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.4
                            }}
                          >
                            {prod.Description}
                          </Typography>
                        )}
                      </Box>

                      {/* Price */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        mt: 0.5
                      }}>
                        {prod.OldPrice && (
                          <Typography
                            component="span"
                            fontFamily="Cairo, sans-serif"
                            sx={{
                              color: 'text.secondary',
                              textDecoration: 'line-through',
                              fontSize: { xs: '0.75rem', sm: '0.85rem' }
                            }}
                          >
                            {prod.OldPrice} YER
                          </Typography>
                        )}
                        {prod.Price && (
                          <Typography
                            component="span"
                            fontFamily="Cairo, sans-serif"
                            fontWeight={700}
                            sx={{
                              color: 'primary.main',
                              fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                          >
                            {prod.Price} YER
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Fade>
      )}

      {/* Empty State */}
      {query.length > 1 && results.length === 0 && !loading && (
        <Fade in>
          <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 }, px: 2 }}>
            <SearchIcon sx={{
              fontSize: { xs: 48, sm: 64 },
              color: alpha(theme.palette.text.secondary, 0.3),
              mb: 2
            }} />
            <Typography
              fontFamily="Cairo, sans-serif"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, mb: 1 }}
            >
              لا توجد نتائج
            </Typography>
            <Typography
              fontFamily="Cairo, sans-serif"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
            >
              جرّب كلمات بحث مختلفة
            </Typography>
          </Box>
        </Fade>
      )}
    </Box>
  );
}

export default SearchBarEnhanced;